import { z } from "zod";
import {
  dbtProjectSubdirRefusal,
  destinationReadinessViewSchema,
  dbtRefRefusal,
  dbtRepoUrlRefusal,
  MAX_DBT_REF_LENGTH,
  MAX_DBT_REPO_URL_LENGTH,
  MAX_DBT_SUBDIR_LENGTH,
  tableNameCaseSchema,
} from "./instances";
import { destinationTypeSchema } from "./destinationType";
import { HOST_PATTERN, slugSchema, tenantPurposeSchema, tenantStatusSchema } from "./tenants";
import { trimRunEnd } from "../trimRuns";
import { WAREHOUSE_TEST_CODES } from "../warehouse/destinationTestAdvice";

export type { DestinationType } from "./destinationType";

/**
 * Instance administration (FR-20, FR-21, FR-22, FR-32): the platform-scope views of an
 * instance, its provisioning job, its destination binding write and its connector tag pins.
 *
 * Two rules hold across every shape here:
 *
 * - **No secret crosses the wire outwards.** A `secret_ref` column is answered as its pointer
 *   only, which is `secretPointerSchema` in `./instances`; the write body below is the one
 *   direction a value travels, and it travels once (SEC-2).
 * - **A create is asynchronous.** `POST /platform/instances` answers 202 with the instance, the
 *   provisioning job and a status URL, because the tenant database does not exist yet when the
 *   response is written (API-STANDARD.md §2).
 */

export const instanceStatusSchema = z.enum(["provisioning", "active", "disabled"]);
export type InstanceStatus = z.infer<typeof instanceStatusSchema>;

export const instanceAdminViewSchema = z.object({
  id: z.uuid(),
  tenantId: z.uuid(),
  /** The tenant's Name (`tenants.name`, platform 0025; the former slug). */
  tenantName: z.string(),
  /** The tenant's Display name (`tenants.display_name`), the name the app shows. */
  tenantDisplayName: z.string(),
  /** Platform 0043 (#824): the tenant's purpose. Optional so an older response still parses. */
  tenantPurpose: tenantPurposeSchema.optional(),
  /**
   * #1294: the tenant's status. Disabling (or deleting, which keeps it `disabled`) a tenant leaves
   * its instances' own status alone, yet none of them can be opened (`isInstanceDisabled`,
   * `server/auth/accessResolver.ts`), so the Instances screen reads this to show the name as plain
   * text. Only a PlatformAdmin's list carries such a row (B-20). Optional so an older response
   * still parses.
   */
  tenantStatus: tenantStatusSchema.optional(),
  name: z.string(),
  slug: z.string(),
  environmentLabel: z.string().nullable(),
  /**
   * FR-22: which PostgreSQL server holds this instance's tenant database. Not a credential.
   *
   * #1191: the placement (this, `tenantDbName` and `tenantDbSchema`) is answered only to a caller
   * holding `platform.instances.manage` (PlatformAdmin). A TenantAdmin's list and read carry none
   * of the three (`withholdPlacement`, `server/services/InstanceService.ts`), so they are optional.
   */
  tenantDbServer: z.string().optional(),
  /** The tenant's database the instance lives in (ADR 0005). PlatformAdmin only (#1191). */
  tenantDbName: z.string().optional(),
  /**
   * The instance's schema in that database (`inst_<slug>_<digest>`), or `public` for an instance
   * still on the old per-instance-database layout. PlatformAdmin only (#1191); optional also so
   * an older response still parses.
   */
  tenantDbSchema: z.string().optional(),
  status: instanceStatusSchema,
  provisionedAt: z.string().nullable(),
  /**
   * Platform 0033: when a PlatformAdmin marked this disabled instance's database or schema gone
   * (`POST /platform/instances/:id/mark-storage-removed`), else null. Optional so an older
   * response still parses.
   */
  storageRemovedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type InstanceAdminView = z.infer<typeof instanceAdminViewSchema>;

export const instancesAdminQuerySchema = z.object({
  tenantId: z.uuid().optional(),
  status: instanceStatusSchema.optional(),
  search: z.string().max(200).optional(),
});
export type InstancesAdminQuery = z.infer<typeof instancesAdminQuerySchema>;

/**
 * Round 4 item 12a: the schema a NEW instance's Snowflake destination loads into. A load is the
 * raw copy of the source (the bronze layer), and a new binding had no schema at all. Only the
 * create writes it (`InstanceService.create`); an existing binding keeps its schema, since moving
 * one starts a new schema (its tables stay behind, incremental watermarks restart, and the
 * customer's dbt sources name the old one).
 */
export const DEFAULT_LOAD_SCHEMA = "BRONZE";

/**
 * Round 5 (owner 2026-10-01): the warehouse database an instance's destination suggests,
 * `<TENANT>_<ENVIRONMENT>` upper case (`NORTHWINDMARKET_PRODUCTION`), so a database in the
 * customer's warehouse says which customer and which environment it holds. A suggestion only:
 * the destination form shows it as the empty field's placeholder and never writes it, as it
 * does with `DEFAULT_LOAD_SCHEMA`. Anything outside A-Z, 0-9 and `_` (an older tenant's dashes,
 * a free-text environment label) becomes `_`, and a name that would start with a digit (the
 * Name of "3M Health" is `3mhealth`) gains a leading `_`, because an unquoted Snowflake
 * identifier starts with a letter or `_`. So the result is always one: `_3MHEALTH_DEVELOPMENT`.
 */
export function suggestedWarehouseDatabase(tenantName: string, environment: string): string {
  const part = (value: string) =>
    value
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "_")
      .replace(/(?:^_+)|(?:_+$)/g, "");
  const name = [part(tenantName), part(environment)].filter((value) => value !== "").join("_");
  return /^[0-9]/.test(name) ? `_${name}` : name;
}

/**
 * FR-20: creating an instance provisions its schema in its tenant's database, binds storage and
 * records the destination type.
 *
 * ADR 0005: one database per tenant, one schema per instance. The tenant's database is
 * chosen when the tenant is created, so an instance never chooses a placement of its own.
 * `tenantDbServer` and `tenantDbName` keep their place in the body (the API shape is unchanged),
 * but their meaning narrowed: when sent, each must equal the tenant's placement (the host compared
 * case-insensitively), and anything else is a 400 naming the tenant's database. Omitted, they are
 * the tenant's. The admin screen no longer sends them.
 */
export const createInstanceBodySchema = z.object({
  tenantId: z.uuid(),
  name: z.string().trim().min(1).max(200),
  slug: slugSchema,
  environmentLabel: z.string().trim().max(64).nullable().optional(),
  /**
   * A host `buildTenantUrl` can actually build a connection string from. Without the pattern a
   * value like `"db 1.internal"` was accepted here, and the throw landed in the runner where
   * nothing recorded it: the job stayed `running` for ever and the instance was bricked with
   * `error: null`.
   */
  tenantDbServer: z
    .string()
    .trim()
    .min(1)
    .max(255)
    .regex(HOST_PATTERN, "Use a host name: letters, digits, dots, dashes and underscores.")
    .optional(),
  /** Postgres identifier rules, and short enough to fit `varchar(63)`. */
  tenantDbName: z
    .string()
    .trim()
    .min(1)
    .max(63)
    .regex(/^[a-zA-Z_][a-zA-Z0-9_$]*$/, "Use a PostgreSQL identifier: letters, digits and _.")
    .optional(),
  destinationType: destinationTypeSchema.default("snowflake"),
  adlsAccount: z.string().trim().max(63).nullable().optional(),
  adlsContainerStaging: z.string().trim().max(63).nullable().optional(),
  adlsContainerLake: z.string().trim().max(63).nullable().optional(),
});
export type CreateInstanceBody = z.infer<typeof createInstanceBodySchema>;

/** The slug and the database placement are immutable; the label and the name are not. */
export const updateInstanceBodySchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    environmentLabel: z.string().trim().max(64).nullable().optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: "Name at least one field to change.",
  });
export type UpdateInstanceBody = z.infer<typeof updateInstanceBodySchema>;

export const disableInstanceBodySchema = z.object({
  reason: z.string().max(500).optional(),
});
export type DisableInstanceBody = z.infer<typeof disableInstanceBodySchema>;

/**
 * Round 9 (B-15): bringing a disabled instance back. The reason is required (a reviewer asks why
 * a customer's environment came back); the instance's schedules stay disabled until a person
 * enables each one, so there is no "run missed schedules" choice.
 */
export const enableInstanceBodySchema = z.object({
  reason: z.string().trim().min(1, "Say why the instance is coming back.").max(500),
});
export type EnableInstanceBody = z.infer<typeof enableInstanceBodySchema>;

/**
 * F36: the Retry of an instance's provisioning job. A DISABLED instance's Retry also brings it
 * back (it goes to provisioning, then active), so it needs a reason under the same rules as
 * Enable; the service refuses a missing one then. An enabled instance's Retry takes none.
 */
export const retryInstanceProvisioningBodySchema = z.object({
  reason: enableInstanceBodySchema.shape.reason.optional(),
});
export type RetryInstanceProvisioningBody = z.infer<typeof retryInstanceProvisioningBodySchema>;

/**
 * Marks a DISABLED instance "storage removed": its database or schema is gone. The service checks
 * that at the moment of marking and refuses (409) when the storage exists or cannot be read.
 */
export const markStorageRemovedBodySchema = z.object({
  reason: z.string().trim().min(1, "Say why the storage is gone.").max(500),
});
export type MarkStorageRemovedBody = z.infer<typeof markStorageRemovedBodySchema>;

/**
 * Whether the write-once archive holds any of an instance's audit chain (platform
 * `audit_exports`, verified day files, `runner/audit/export.ts`): `present` or `none`. A mark is
 * allowed either way (the storage is already gone); `none` means that history cannot be recovered.
 */
export const auditArchiveStateSchema = z.enum(["present", "none"]);
export type AuditArchiveState = z.infer<typeof auditArchiveStateSchema>;

/**
 * `POST /platform/instances/:id/mark-storage-removed`: the instance, and what the archive held.
 * `present` only says some archive day exists, so `archiveThrough` (`YYYY-MM-DD`, null with
 * `none`) names the last day it reaches and `archiveRows` how many rows it keeps (0 with `none`).
 */
export const markStorageRemovedResponseSchema = instanceAdminViewSchema.extend({
  archive: auditArchiveStateSchema,
  archiveThrough: z.string().nullable(),
  archiveRows: z.number().int().nonnegative(),
});
export type MarkStorageRemovedResponse = z.infer<typeof markStorageRemovedResponseSchema>;

/**
 * `GET /platform/instances/:id/storage-removal-check` (read only, `platform.instances.manage`):
 * what the mark's confirm dialog shows before anyone marks. `through` (`YYYY-MM-DD`) and `rows`
 * describe the archive when there is one; counts and a date only (SEC-17).
 */
export const storageRemovalCheckSchema = z.object({
  archive: auditArchiveStateSchema,
  through: z.string().nullable(),
  rows: z.number().int().nonnegative(),
});
export type StorageRemovalCheck = z.infer<typeof storageRemovalCheckSchema>;

export const provisioningStatusSchema = z.enum(["queued", "running", "succeeded", "failed"]);
export type ProvisioningStatus = z.infer<typeof provisioningStatusSchema>;

/**
 * One recorded provisioning step. The names are the runner's own and are stable, because the
 * admin screen renders them as a vertical stepper. An instance job (ADR 0005) records
 * `ensure_tenant_db`, `create_schema`, `grant_schema`, `migrate_instance`, `write_binding`,
 * `activate`; jobs from before S8 recorded `create_database`, `grant_roles`, `migrate_tenant`,
 * `write_binding`, `activate`, and still render from history. A step waiting on another job
 * for the same tenant database is `pending` with the reason in `detail`.
 */
export const provisioningStepSchema = z.object({
  name: z.string(),
  status: z.enum(["pending", "running", "succeeded", "failed", "skipped"]),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  /** A sentence, never a connection string and never a credential. */
  error: z.string().nullable(),
  /** A short non-sensitive note, such as "already existed". */
  detail: z.string().nullable(),
  /**
   * True when the step did nothing because `DATABASE_URL_TENANT_DEV` points every instance at
   * one shared database — a developer machine, never a deployment. It is here so the stepper
   * can badge that skip as the override it is: rendered as a plain `skipped` it reads as a
   * database that happened to be there already, which is the one case that must alarm.
   */
  sharedDevDatabase: z.boolean().optional(),
});
export type ProvisioningStep = z.infer<typeof provisioningStepSchema>;

export const provisioningJobViewSchema = z.object({
  id: z.uuid(),
  /**
   * What the job builds (ADR 0010): `instance` (and `convert_instance`) jobs name an instance;
   * `tenant_db` and `tenant_resources` jobs name a tenant and no instance.
   */
  kind: z.enum(["instance", "tenant_db", "convert_instance", "tenant_resources"]),
  /** Null for a `tenant_resources` (or `tenant_db`) job. */
  instanceId: z.uuid().nullable(),
  /** The tenant a tenant job is for; an instance job's row may carry none. */
  tenantId: z.uuid().nullable(),
  /** A `tenant_resources` job's intent; every other job is `apply`. */
  action: z.enum(["apply", "suspend"]),
  status: provisioningStatusSchema,
  steps: z.array(provisioningStepSchema),
  error: z.string().nullable(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Where to poll. Present so the client never builds the URL itself. */
  statusUrl: z.string(),
});
export type ProvisioningJobView = z.infer<typeof provisioningJobViewSchema>;

/** The 202 body of `POST /platform/instances`. */
export const createInstanceResponseSchema = z.object({
  instance: instanceAdminViewSchema,
  job: provisioningJobViewSchema,
  statusUrl: z.string(),
});
export type CreateInstanceResponse = z.infer<typeof createInstanceResponseSchema>;

/* ------------------------------------------------------------------------------------- *
 * Connector tag pins (FR-32)
 * ------------------------------------------------------------------------------------- */

export const connectorPinViewSchema = z.object({
  instanceId: z.uuid(),
  connectorName: z.string(),
  tag: z.string(),
  updatedAt: z.string(),
});
export type ConnectorPinView = z.infer<typeof connectorPinViewSchema>;

export const putConnectorPinBodySchema = z.object({
  /** Must be a tag the definition actually carries; the route checks it, not the column. */
  tag: z.string().trim().min(1).max(128),
});
export type PutConnectorPinBody = z.infer<typeof putConnectorPinBodySchema>;

/**
 * The two path parameters of a pin route. Named for the pair rather than for the connector,
 * because `shared/types/connectors.ts` already exports a `connectorNameParamsSchema` of a
 * different shape and the barrel re-exports that one.
 */
export const instanceConnectorParamsSchema = z.object({
  id: z.uuid(),
  connectorName: z
    .string()
    .trim()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9][a-z0-9_-]*$/, "A connector name is lowercase letters, digits, - and _."),
});
export type InstanceConnectorParams = z.infer<typeof instanceConnectorParamsSchema>;

/* ------------------------------------------------------------------------------------- *
 * Destination binding write (FR-21, FR-14, SEC-2, SEC-4)
 * ------------------------------------------------------------------------------------- */

/**
 * `secrets.adlsKey`, the Snowflake stage SAS, is retired (#399, owner 2026-10-12: per-tenant only,
 * loads push, ADR 0013): a body that still names it is refused with 400 on `secrets.adlsKey`
 * rather than having the value dropped unread.
 */
const retiredStageSasSchema = z
  .never({ error: "The stage SAS token is retired: loads no longer read one." })
  .optional();

/**
 * The secret-bearing fields of a binding, by the name the write body uses and the column they
 * land in. Exported because three things have to agree about this list — the write body, the
 * vault-first writer and the audit metadata that names which secrets changed — and a list in
 * one place is the only way they cannot drift.
 */
export const BINDING_SECRET_FIELDS = [
  "snowflakePrivateKey",
  "snowflakeKeyPassphrase",
  "databricksSpSecret",
  "dbtGitSecret",
  "teamsWebhook",
] as const;
export type BindingSecretField = (typeof BINDING_SECRET_FIELDS)[number];

/**
 * A secret value on the way in, or `null` to clear the pointer.
 *
 * `undefined` (the key absent) means "leave the stored secret alone", which is what the screen
 * sends for every field it renders as "Stored" — so a save never has to round-trip a value it
 * was never given. The bound is generous because a Snowflake key-pair PEM is a few kilobytes.
 */
const secretValueSchema = z.string().min(1).max(20_000).nullable();

/** Azure Key Vault's limit on a secret name (`MAX_SECRET_NAME_LENGTH` on the server). */
export const COPY_SOURCE_NAME_MAX = 127;

/**
 * The Key Vault naming rule, for the client's own check before a copy is staged (the server's
 * `isValidSecretName` is the authority and uses the same pattern).
 */
export const COPY_SOURCE_NAME_PATTERN = /^[a-zA-Z0-9-]{1,127}$/;

/**
 * B28 (bind pack 1): copy mode. The name of a secret already in the deployment's own vault,
 * whose value the server copies into a NEW secret owned by the target instance (the owner's
 * ruling of 2026-09-26: each instance keeps its own copy, never a shared pointer).
 *
 * Strict on both levels, so a vault name or a version is refused rather than ignored: the vault
 * is always the deployment's (`SecretStore.vaultName`) and the latest version is what is copied.
 * The naming rule itself is the server's (`isValidSecretName`), which answers with the sentence
 * `copySourceNameMessage` builds on `secrets.<field>`; this schema only bounds the input.
 */
export const bindingSecretCopySchema = z
  .object({
    // At most Key Vault's 127 characters (review r1): anything longer cannot be a name, and
    // zod's generic sentence does not repeat it, so a value pasted into the name box by mistake
    // (a PEM, a long token) is never echoed back by `copySourceNameMessage`.
    copyFrom: z.object({ name: z.string().trim().min(1).max(COPY_SOURCE_NAME_MAX) }).strict(),
  })
  .strict();
export type BindingSecretCopy = z.infer<typeof bindingSecretCopySchema>;

/** What the platform route takes for a secret field: a value, null to clear, or a copy. */
const platformSecretInputSchema = z.union([secretValueSchema, bindingSecretCopySchema]);

/** True for the copy-mode shape of a secret field. */
export function isBindingSecretCopy(value: unknown): value is BindingSecretCopy {
  return (
    typeof value === "object" &&
    value !== null &&
    "copyFrom" in value &&
    typeof (value as { copyFrom?: { name?: unknown } }).copyFrom?.name === "string"
  );
}

/** B28: the 400 for a copy source the deployment's vault does not hold (or cannot read). */
export function copySourceMissingMessage(name: string): string {
  return `No secret named ${name} is readable in the deployment's vault.`;
}

/** B28: the 400 for a copy source name that breaks the Key Vault naming rule. */
export function copySourceNameMessage(name: string): string {
  return `${name} is not a valid secret name.`;
}

/** The platform body's twin of the refine on `updateBindingBodySchema`: name at least one field. */
function namesAtLeastOneField(value: { secrets?: object }): boolean {
  return (
    Object.keys(value).length > 0 &&
    (Object.keys(value).some((key) => key !== "secrets") ||
      Object.keys(value.secrets ?? {}).length > 0)
  );
}

export const updateBindingBodySchema = z
  .object({
    destinationType: destinationTypeSchema.optional(),
    adlsAccount: z.string().trim().max(63).nullable().optional(),
    adlsContainerStaging: z.string().trim().max(63).nullable().optional(),
    adlsContainerLake: z.string().trim().max(63).nullable().optional(),
    snowflakeAccount: z.string().trim().max(255).nullable().optional(),
    snowflakeWarehouse: z.string().trim().max(255).nullable().optional(),
    snowflakeDatabase: z.string().trim().max(255).nullable().optional(),
    snowflakeSchema: z.string().trim().max(255).nullable().optional(),
    snowflakeRole: z.string().trim().max(255).nullable().optional(),
    snowflakeUser: z.string().trim().max(255).nullable().optional(),
    snowflakeStorageIntegration: z.string().trim().max(255).nullable().optional(),
    snowflakeStageName: z.string().trim().max(255).nullable().optional(),
    /**
     * Round 6 (owner 2026-10-01 23:10): `native` or `source`. Absent leaves it. `source` on a
     * Databricks binding is refused by `BindingService` (`KEEP_SOURCE_CASE_UNAVAILABLE`).
     */
    tableNameCase: tableNameCaseSchema.optional(),
    /** #2466: add `_SKYDATA_SYNCED_AT` and `_SKYDATA_RUN_ID` to every Snowflake table. Absent leaves it. */
    metadataColumns: z.boolean().optional(),
    databricksHost: z.string().trim().max(255).nullable().optional(),
    databricksSpClientId: z.string().trim().max(255).nullable().optional(),
    databricksWarehouseId: z.string().trim().max(255).nullable().optional(),
    /**
     * Refused at the WRITE, not at the bundle (fix-round answer 35).
     *
     * `https` only and no userinfo, because the git token is `instance_bindings.dbt_git_secret`
     * and every git client documents an HTTPS clone as `https://user:pat@host/repo.git` — so the
     * one shape an admin is most likely to paste is the one that would put a live credential in
     * a platform column, in the hash-chained audit snapshot, and on the wire to every principal
     * with binding read access (CLAUDE.md: no credential in any database column). The refusal
     * message comes from `dbtRepoUrlRefusal`, a fixed sentence that never echoes the value (#1250),
     * so the `VALIDATION_FAILED` envelope cannot become the second place the token lives.
     */
    dbtRepoUrl: z
      .string()
      .trim()
      .max(MAX_DBT_REPO_URL_LENGTH)
      .nullable()
      .optional()
      .superRefine((value, ctx) => {
        // An absent key leaves the column alone and an empty string clears it; neither is a URL.
        if (value === undefined || value === null || value === "") return;
        const refusal = dbtRepoUrlRefusal(value);
        if (refusal) ctx.addIssue({ code: "custom", message: refusal });
      }),
    /**
     * The git ref the transform checks out: a branch, a tag or a 40-hex sha (the column keeps
     * its `dbt_branch` name). Refused at the write with the runner's own rule, `dbtRefRefusal`,
     * so a ref `git fetch` would read as a flag or refuse to resolve fails in the editor rather
     * than in the clone phase of the next scheduled run. Absent leaves it; empty or null clears
     * it, and the runner then checks out `main`.
     */
    dbtBranch: z
      .string()
      .trim()
      .max(MAX_DBT_REF_LENGTH)
      .nullable()
      .optional()
      .superRefine((value, ctx) => {
        if (value === undefined || value === null || value === "") return;
        const refusal = dbtRefRefusal(value);
        if (refusal) ctx.addIssue({ code: "custom", message: refusal });
      }),
    /**
     * Where `dbt_project.yml` lives inside the clone, relative to its root (platform migration
     * 0005). The runner already honoured the column; this is the field that lets the destination
     * screen set it. Refused with `dbtProjectSubdirRefusal` — no leading `/`, no `..` — because
     * the container joins it onto the clone and runs dbt with `--project-dir`. Absent leaves it;
     * empty or null clears it, which means the repository root.
     */
    dbtProjectSubdir: z
      .string()
      .trim()
      .max(MAX_DBT_SUBDIR_LENGTH)
      .nullable()
      .optional()
      .superRefine((value, ctx) => {
        if (value === undefined || value === null || value === "") return;
        const refusal = dbtProjectSubdirRefusal(value);
        if (refusal) ctx.addIssue({ code: "custom", message: refusal });
      }),
    /**
     * Round 9 (B-02): confirms that a save which moves the destination (type, account, database
     * or schema) while tables have already landed also clears every table's load bound, so each
     * connection reads everything again. Without it such a save is asked to confirm with 409
     * `DESTINATION_RESET_CONFIRM` on the field `resetBounds`, a confirm step that writes no
     * failed audit row (#1461). Never stored.
     */
    resetBounds: z.boolean().optional(),
    /** Values, not pointers: the only direction a secret travels (SEC-2). */
    secrets: z
      .object({
        adlsKey: retiredStageSasSchema,
        snowflakePrivateKey: secretValueSchema.optional(),
        snowflakeKeyPassphrase: secretValueSchema.optional(),
        databricksSpSecret: secretValueSchema.optional(),
        dbtGitSecret: secretValueSchema.optional(),
        teamsWebhook: secretValueSchema.optional(),
      })
      .optional(),
  })
  .refine(
    (value) =>
      Object.keys(value).length > 0 &&
      (Object.keys(value).some((key) => key !== "secrets") ||
        Object.keys(value.secrets ?? {}).length > 0),
    { message: "Name at least one field to change." },
  );
export type UpdateBindingBody = z.infer<typeof updateBindingBodySchema>;

/**
 * `PUT /platform/instances/:id/binding` (B28): the same body, except that each secret field
 * may also be `{ copyFrom: { name } }`. Only the platform route parses this schema; the two
 * instance-level routes keep the value-only schemas above and refuse the object with 400
 * `VALIDATION_FAILED` on `secrets.<field>`.
 */
export const platformUpdateBindingBodySchema = z
  .object({
    ...updateBindingBodySchema.shape,
    secrets: z
      .object({
        adlsKey: retiredStageSasSchema,
        snowflakePrivateKey: platformSecretInputSchema.optional(),
        snowflakeKeyPassphrase: platformSecretInputSchema.optional(),
        databricksSpSecret: platformSecretInputSchema.optional(),
        dbtGitSecret: platformSecretInputSchema.optional(),
        teamsWebhook: platformSecretInputSchema.optional(),
      })
      .optional(),
  })
  .refine(namesAtLeastOneField, { message: "Name at least one field to change." });
export type PlatformUpdateBindingBody = z.infer<typeof platformUpdateBindingBodySchema>;

/* ------------------------------------------------------------------------------------- *
 * Session 10: the transformation source (dbt) and the Connect probe (B14-B16, P5)
 * ------------------------------------------------------------------------------------- */

/**
 * The storage columns the instance route refuses (P5). Storage is provisioned by the platform
 * and written through `PUT /platform/instances/:id/binding` only; the instance route answers
 * 400 naming the field rather than stripping it, so a caller learns the value was not written.
 * `secrets.adlsKey` (the Snowflake stage SAS) is retired on both (#399, `retiredStageSasSchema`).
 */
export const PLATFORM_STORAGE_FIELDS = [
  "adlsAccount",
  "adlsContainerStaging",
  "adlsContainerLake",
] as const;
export const STORAGE_MANAGED_MESSAGE = "Storage is managed by the platform.";

/** B5: a changed repository never inherits the stored token (the dbt PUT). */
export const DBT_NEW_REPO_TOKEN_MESSAGE =
  "Enter the access token for the new repository. The saved token only works for the saved repository.";
/**
 * The builtin sample project's sentinel address (`runner/executors/DbtJobExecutor.ts`). A binding
 * naming it, on a runner with `SECRET_STORE=local`, runs the sample project with no clone. A
 * `.invalid` host (RFC 2606), so a deployment without the local gate fails to clone it.
 */
export const BUILTIN_DBT_REPO_URL = "https://builtin.skydata.invalid/sample-project.git";
/** B5: Connect reads the stored token only for the stored repository. */
export const DBT_PROBE_TOKEN_MESSAGE =
  "Enter the access token for this repository. The saved token is only used for the saved repository.";
/** Q4: cloud hosts only; saving another host is still allowed. */
export const DBT_PROBE_HOST_MESSAGE =
  "Connect supports GitHub and Azure DevOps repositories. You can still save this address.";

/**
 * Removes a username-only userinfo from a repository address (SF5).
 *
 * Azure DevOps' clone box hands out `https://<org>@dev.azure.com/<org>/<project>/_git/<repo>`.
 * The `<org>@` is not a credential, but `dbtRepoUrlRefusal` refuses any userinfo, so the first
 * address an owner pastes would be refused as one. A username alone is removed; a password is
 * NEVER removed, because `user:secret@` is a live credential and must still be refused rather
 * than silently cleaned into a value that looks safe. Done on the raw string with one anchored
 * pattern so nothing else in the address is re-encoded. `removed` is the username that went,
 * for the screen's note; the server never echoes it.
 */
export function stripDbtRepoUrlUsername(url: string): { url: string; removed: string | null } {
  const match = /^(https?:\/\/)([^/?#@:]+)@/i.exec(url);
  if (!match) return { url, removed: null };
  return { url: `${match[1]}${url.slice(match[0].length)}`, removed: match[2] ?? null };
}

/**
 * The comparison form of a repository address (B5): scheme and host lowercased, a username-only
 * userinfo removed, a trailing `/` or `.git` removed and the path compared case-insensitively.
 * Two addresses that normalise to the same string are the same repository for the rule that
 * the stored token is only ever sent to the stored repository.
 */
export function normaliseDbtRepoUrlForCompare(url: string | null | undefined): string | null {
  if (url === null || url === undefined) return null;
  const trimmed = stripDbtRepoUrlUsername(url.trim()).url;
  if (trimmed === "") return null;
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return trimmed.toLowerCase();
  }
  let path = parsed.pathname.toLowerCase();
  for (;;) {
    const next = trimRunEnd(path, "/").replace(/\.git$/, "");
    if (next === path) break;
    path = next;
  }
  return `${parsed.protocol}//${parsed.host}${path}`;
}

/** Trims and removes a username-only userinfo before the shared refusal runs (SF5). */
function preprocessRepoUrl(value: unknown): unknown {
  return typeof value === "string" ? stripDbtRepoUrlUsername(value.trim()).url : value;
}

const dbtRepoUrlField = z.preprocess(
  preprocessRepoUrl,
  z
    .string()
    .trim()
    .max(MAX_DBT_REPO_URL_LENGTH)
    .nullable()
    .optional()
    .superRefine((value, ctx) => {
      if (value === undefined || value === null || value === "") return;
      const refusal = dbtRepoUrlRefusal(value);
      if (refusal) ctx.addIssue({ code: "custom", message: refusal });
    }),
);

const dbtRefField = z
  .string()
  .trim()
  .max(MAX_DBT_REF_LENGTH)
  .nullable()
  .optional()
  .superRefine((value, ctx) => {
    if (value === undefined || value === null || value === "") return;
    const refusal = dbtRefRefusal(value);
    if (refusal) ctx.addIssue({ code: "custom", message: refusal });
  });

const dbtSubdirField = z
  .string()
  .trim()
  .max(MAX_DBT_SUBDIR_LENGTH)
  .nullable()
  .optional()
  .superRefine((value, ctx) => {
    if (value === undefined || value === null || value === "") return;
    const refusal = dbtProjectSubdirRefusal(value);
    if (refusal) ctx.addIssue({ code: "custom", message: refusal });
  });

/**
 * `PUT /instances/current/binding/dbt` (P5). Strict: a Warehouse or storage key sent here is a
 * 400, never stripped, because this route is open to `dbt_jobs.write` (InstanceAdmin)
 * while `instance.binding.write` stays PlatformAdmin-only.
 */
export const updateDbtSourceBodySchema = z
  .object({
    dbtRepoUrl: dbtRepoUrlField,
    dbtBranch: dbtRefField,
    dbtProjectSubdir: dbtSubdirField,
    secrets: z.object({ dbtGitSecret: secretValueSchema.optional() }).strict().optional(),
  })
  .strict()
  .refine(
    (value) =>
      Object.keys(value).some((key) => key !== "secrets") ||
      Object.keys(value.secrets ?? {}).length > 0,
    { message: "Name at least one field to change." },
  );
export type UpdateDbtSourceBody = z.infer<typeof updateDbtSourceBodySchema>;

/**
 * #923: `POST /instances/current/binding/test`. `test` may move the destination to Ready or back
 * to Draft; `check` (Check now) only records the check, so a Ready destination only warns.
 */
export const destinationTestBodySchema = z.object({ mode: z.enum(["test", "check"]) }).strict();
export type DestinationTestBody = z.infer<typeof destinationTestBodySchema>;

/** What a destination test answers: the code's sentence, next step and SQL, and the new state. */
export const destinationTestViewSchema = z.object({
  mode: z.enum(["test", "check"]),
  code: z.enum(WAREHOUSE_TEST_CODES),
  passed: z.boolean(),
  sentence: z.string(),
  nextStep: z.string().nullable(),
  sql: z.array(z.string()),
  testedAt: z.string(),
  readiness: destinationReadinessViewSchema,
  driver: z.object({ code: z.string().nullable(), queryId: z.string().nullable() }).nullable(),
});
export type DestinationTestView = z.infer<typeof destinationTestViewSchema>;

/** `POST /instances/current/binding/dbt/probe` (P5). The token is optional; see B5. */
export const dbtProbeBodySchema = z
  .object({
    dbtRepoUrl: z.preprocess(
      preprocessRepoUrl,
      z
        .string()
        .min(1)
        .max(MAX_DBT_REPO_URL_LENGTH)
        .superRefine((value, ctx) => {
          const refusal = dbtRepoUrlRefusal(value);
          if (refusal) ctx.addIssue({ code: "custom", message: refusal });
        }),
    ),
    dbtBranch: dbtRefField,
    dbtProjectSubdir: dbtSubdirField,
    dbtGitSecret: z.string().min(1).max(20_000).optional(),
  })
  .strict();
export type DbtProbeBody = z.infer<typeof dbtProbeBodySchema>;

export const DBT_PROBE_REASONS = [
  "auth",
  "not_found",
  "redirect",
  "timeout",
  "network",
  "not_git",
] as const;
export const dbtProbeReasonSchema = z.enum(DBT_PROBE_REASONS);
export type DbtProbeReason = z.infer<typeof dbtProbeReasonSchema>;

export const MAX_PROBE_BRANCHES = 500;
export const MAX_PROBE_TAGS = 200;

/**
 * What Connect answers (P5). Anything the remote said is a 200 with `ok: false` and a reason;
 * the token is never part of it, and the audit row keeps counts only (SF3).
 */
export const dbtProbeResultSchema = z.object({
  ok: z.boolean(),
  reason: dbtProbeReasonSchema.nullable(),
  host: z.enum(["github", "azure_devops"]),
  defaultBranch: z.string().nullable(),
  branches: z.array(z.string()).max(MAX_PROBE_BRANCHES),
  tags: z.array(z.string()).max(MAX_PROBE_TAGS),
  /** The ref the tree and the project file were read at. */
  ref: z.string(),
  /** The sha that ref resolved to, or null when nothing resolved. */
  commit: z.string().nullable(),
  /** Folders holding a `dbt_project.yml` (`""` is the root); null when the tree was not read. */
  projectDirs: z.array(z.string()).nullable(),
  treeTruncated: z.boolean(),
  project: z
    .object({
      subdir: z.string(),
      profile: z.string().nullable(),
      profileNote: z.string().nullable(),
      requireDbtVersion: z.string().nullable(),
      versionOk: z.boolean().nullable(),
    })
    .nullable(),
  warnings: z.array(z.string()),
  usedStoredSecret: z.boolean(),
  /**
   * #1674: whether the ref asked for is a branch or tag the repository advertises (a full commit
   * id counts as found: the advertisement lists tips only). Null when no ref was asked for or the
   * refs could not be read; absent from an answer built before the field.
   */
  refFound: z.boolean().nullable().optional(),
});
export type DbtProbeResult = z.infer<typeof dbtProbeResultSchema>;
